import { jsx, jsxs } from 'react/jsx-runtime';
import React__default from 'react';
import { CheckCircleIcon } from '@iris/react-icons/CheckCircleIcon';
import { ExclamationCircleIcon } from '@iris/react-icons/ExclamationCircleIcon';
import { InformationCircleIcon } from '@iris/react-icons/InformationCircleIcon';
import { XMarkIcon } from '@iris/react-icons/XMarkIcon';
import { ControlButton } from '../ControlButton/ControlButton.esm.js';
import styles from './Toast.styles.esm.js';
import { ToastAction } from './ToastAction.esm.js';
import { ToastDescription } from './ToastDescription.esm.js';
import { ToastProvider } from './ToastProvider.esm.js';
import { ToastRoot } from './ToastRoot.esm.js';
import { ToastTitle } from './ToastTitle.esm.js';
import { ToastViewport } from './ToastViewport.esm.js';
import { cn } from '../../utils/cn.esm.js';

const Toast = React__default.forwardRef(({ className, type = "info", title, showTitle = true, message, action, open, duration = 5, onOpenChange, swipeDirection = "down", }, ref) => {
    const { viewport, root, icon: iconClass, container, titleBox, messageBox, content, title: titleClass, message: messageClass, closeButton, link: linkClass, } = styles({ type: type, swipeDirection });
    // Default icons based on variant if no custom icon is provided
    const Icon = React__default.useMemo(() => {
        switch (type) {
            case "confirm":
                return jsx(CheckCircleIcon, { "aria-hidden": "true" });
            case "error":
                return jsx(ExclamationCircleIcon, { "aria-hidden": "true" });
            case "info":
            default:
                return jsx(InformationCircleIcon, { "aria-hidden": "true" });
        }
    }, [type]);
    return (jsxs(ToastProvider, { swipeDirection: swipeDirection, children: [jsxs(ToastRoot, { ref: ref, className: cn(root(), className), duration: duration * 1000, open: open, onOpenChange: onOpenChange, role: "alert", "aria-live": "assertive", children: [jsx("div", { className: "iris:sr-only", children: `${title && showTitle ? title : ""} ${message}` }), jsxs("div", { className: container(), children: [jsx("div", { className: iconClass(), children: Icon }), jsxs("div", { className: content(), children: [title && showTitle && (jsx(ToastTitle, { className: titleBox(), "aria-hidden": true, children: jsx("span", { className: titleClass(), children: title }) })), jsxs("div", { className: messageBox(), children: [jsx(ToastDescription, { className: messageClass(), "aria-hidden": true, children: message }), action && (jsx(ToastAction, { asChild: true, altText: "Perform action", children: React__default.isValidElement(action)
                                                    ? React__default.cloneElement(action, {
                                                        className: cn(action.props.className, linkClass()), // override style
                                                    })
                                                    : action }))] })] })] }), jsx(ToastAction, { asChild: true, altText: `Close notification`, className: closeButton(), "aria-label": `Close notification`, children: jsx(ControlButton, { inverse: true, icon: jsx(XMarkIcon, {}) }) })] }), jsx(ToastViewport, { className: viewport() })] }));
});
Toast.displayName = "Toast";
// Attach subcomponents to the main Toast component
Toast.Provider = ToastProvider;
Toast.Root = ToastRoot;
Toast.Title = ToastTitle;
Toast.Description = ToastDescription;
Toast.Action = ToastAction;
Toast.Viewport = ToastViewport;

export { Toast, ToastAction, ToastDescription, ToastProvider, ToastRoot, ToastTitle, ToastViewport };
